import { deriveWorkState, Task, TaskListItem, TaskStatus } from "@myapp/contract";
import { ISelectOption } from "../../../shared/types/ISelectOption";

export const NEEDS_RESCHEDULING_LABEL = "Braucht Umdisponierung";

/**
 * Umdisponierung is not one of the four statuses — it is a flag on the reservation that outranks
 * whatever the status says while it is set, so it carries its own colour rather than borrowing
 * one. The same red the Termine table paints its rescheduling badge in.
 */
export const NEEDS_RESCHEDULING_COLOR = "#EF4444";

const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  PLANNED: "Geplant",
  COMPLETED: "Abgeschlossen",
  DELAYED: "Verzögert",
  CANCELLED: "Storniert",
};

const IN_PROGRESS_COLOR = "#ED9E42";

type TaskLike = Pick<Task | TaskListItem, 'status' | 'schedule'>

/**
 * Something is happening on this Aufgabe right now. One rule for the whole app —
 * `deriveWorkState` in the contract — so the list, the board, the worker calendar and the server's
 * own in-progress queries cannot disagree about it. Status and phase are read together: a
 * COMPLETED task whose timestamps were never closed is not running, it is a leftover.
 */
export const isTaskRunning = (t: TaskLike): boolean => deriveWorkState(t).isRunning;

/**
 * Pass the task, not just its status, wherever you have one: without it the label can only ever
 * say what the status says, and a running Aufgabe reads as „Geplant“.
 */
export const getTaskStatusLabel = (
  status: TaskStatus,
  task?: TaskLike,
): string => {
  if (task) {
    const { label } = deriveWorkState(task);
    if (label) return label;
  }
  return TASK_STATUS_LABELS[status];
};

const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  PLANNED: "#2EB5D9",
  COMPLETED: "#3BC67D",
  DELAYED: "#ED9E42",
  CANCELLED: "#9CA3AF",
};

export const getTaskStatusColor = (
  status: TaskStatus,
  task?: TaskLike,
): string => {
  // Every open phase reads as "in progress", not only the work phase — the same amber for
  // "Anfahrt läuft" and "Abfahrt läuft" as for "In Bearbeitung".
  if (task && deriveWorkState(task).isRunning) return IN_PROGRESS_COLOR;
  return TASK_STATUS_COLORS[status];
};

export const taskStatusOptions: ISelectOption[] = (
  Object.keys(TASK_STATUS_LABELS) as TaskStatus[]
).map((status) => ({ value: status, label: TASK_STATUS_LABELS[status] }));

export type StatusFilterValue = TaskStatus | "NEEDS_RESCHEDULING";

export const taskStatusFilterOptions: ISelectOption[] = [
  { value: "PLANNED", label: TASK_STATUS_LABELS.PLANNED },
  { value: "COMPLETED", label: TASK_STATUS_LABELS.COMPLETED },
  { value: "NEEDS_RESCHEDULING", label: NEEDS_RESCHEDULING_LABEL },
];
